import React, { useState } from 'react';
import { BsArrowUpRight } from 'react-icons/bs';
import { Button, Spinner, ConfirmModal } from 'components';
import { observer } from 'mobx-react';

import { formatDate } from 'utils/formatStrings';
import { clientStore } from 'apps/client/app';
import { Link } from 'core/routing';
import { staticURL } from 'core/url';
import i18next from 'i18next';

type FileSideBarProps = {
  project: any;
  contributionsConn: any;
  isNotLocallyConnected?: boolean;
  file: DriveTableDataSource;
  onRestoreFileToVersion: (
    file: DriveTableDataSource,
    contribution: any,
  ) => void;
  onClosePanel: () => void;
};

export const FileHistory = observer(
  ({
    project,
    file,
    contributionsConn,
    onRestoreFileToVersion,
    onClosePanel,
    isNotLocallyConnected,
  }: FileSideBarProps) => {
    const [contributionForRestoreModal, setContributionForRestoreModal] =
      useState(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);

    const contributions = contributionsConn?.arrayApp || [];

    return (
      <div className='flex flex-col gap-1'>
        {contributions.map((contribution: any, index: number, arr: any[]) => {
          const previousContribution = arr[index + 1];

          const shortContributionVersion = contribution.version.substring(0, 7);
          const contributionVersionString = `#${shortContributionVersion}`;

          let canCompare = false;

          const currentFile = contribution.files.find(
            (f: any) => f.path === file.id,
          );

          const wasFileDeletedInThisContribution = !currentFile;

          // if the file has been deleted in this contribution it doesn't have a repoRevision
          // but we still want to show a version (the same as the previous contribution in time)
          // so we will calculate that in this variable.
          let deletedFileRepoRevision = 0;

          let previousFile = null;
          let compareFilesVersionsRouteParams = null;
          if (previousContribution) {
            previousFile = previousContribution.files.find(
              (f: any) => f.path === file.id,
            );
            // we can fail to find a file if that file was deleted in that contribution
            // in that case we should not show compare.
            canCompare = previousFile && currentFile;
            if (canCompare) {
              compareFilesVersionsRouteParams = {
                ...project.route.params,
                contributionId: shortContributionVersion,
                basepath: file?.id,
                diff: window.encodeURIComponent(previousFile.file.id),
                repoId: project.id,
                isCloudDiffFile: true,
              };
            }

            if (wasFileDeletedInThisContribution && previousFile) {
              deletedFileRepoRevision = previousFile.repoRevision;
            }
          } else {
            // eslint-disable-next-line no-lonely-if
            if (wasFileDeletedInThisContribution) {
              // We don't have a previous contribution but we need to calculate
              // the repoRevision for this deleted file so go to the nextContribution
              const nextContribution = arr[index - 1];
              if (nextContribution) {
                const nextFile = nextContribution.files.find(
                  (f: any) => f.path === file.id,
                );
                if (nextFile) {
                  deletedFileRepoRevision = nextFile.repoRevision - 1;
                }
              }
            }
          }

          const repoRevisionString = currentFile
            ? `v${currentFile.repoRevision}`
            : `v${deletedFileRepoRevision}`;

          const contribRoute = `${clientStore.spaceRoute.route}.contribution`;
          const params = Object.assign({}, clientStore.spaceParams, {
            contributionId: shortContributionVersion,
          });

          const fileVersionRouteParams = {
            ...project.route.params,
            contributionId: shortContributionVersion,
            basepath: file?.id,
          };

          const fileVersionRouteName = `${project.route.name}.version.file`;

          const didFileExistInThisContribution = !!currentFile;
          const shouldRenderRestoreButton =
            !isNotLocallyConnected &&
            index > 0 &&
            didFileExistInThisContribution;

          let redText = '';
          if (!didFileExistInThisContribution) {
            redText = 'text-wfRed900';
          }

          return (
            <div
              key={contribution.id}
              className='flex flex-col gap-1 text-14'
              style={{ border: '1p solid red' }}
            >
              <div className='flex items-center justify-between gap-3'>
                <Link
                  name={contribRoute}
                  params={params}
                  openTab={{
                    title: contributionVersionString,
                    iconType: 'project',
                  }}
                >
                  <div
                    className={`min-w-[1.25rem] min-h-[1.25rem] strike-throu py-1 px-2 text-center border border-black text-black hover:bg-gray-100 ${redText}`}
                  >
                    {repoRevisionString}
                  </div>
                </Link>
                <div className='flex flex-col'>
                  <div className='text-gray-600'>
                    {formatDate(contribution.dateCreated)}
                  </div>
                  <div className='text-gray-600'>
                    by {contribution.creatorProfile.username}
                  </div>
                </div>
              </div>

              <div className='font-primary font-extrabold'>
                {contribution.title}
              </div>
              <div className='text-gray-600'>{contribution.description}</div>
              {wasFileDeletedInThisContribution && (
                <div className='flex justify-center text-wfRed900'>
                  - File removed -
                </div>
              )}
              <div className='flex justify-end gap-4 items-center'>
                {shouldRenderRestoreButton && (
                  <div
                    onClick={() => {
                      setContributionForRestoreModal(contribution);
                      setShowRestoreModal(true);
                    }}
                    className='flex gap-1 items-center cursor-pointer text-black py-1 px-2 rounded-lg hover:bg-gray-100'
                  >
                    Restore <img src={staticURL('assets/icons/reset.svg')} />
                  </div>
                )}
                {!wasFileDeletedInThisContribution && (
                  <Link
                    name={fileVersionRouteName}
                    params={fileVersionRouteParams}
                    openTab={{
                      title: file?.filename,
                      iconType: 'file',
                    }}
                  >
                    <div className='flex gap-1 items-center cursor-pointer text-black py-1 px-2 rounded-lg hover:bg-gray-100'>
                      Open <BsArrowUpRight />
                    </div>
                  </Link>
                )}
              </div>

              {canCompare ? (
                <div className='flex gap-2 items-center my-4'>
                  Compare{' '}
                  {/* {`${previousRevisionNumber} - ${currentRevisionNumber}`}{' '} */}
                  <Link
                    name={fileVersionRouteName}
                    params={compareFilesVersionsRouteParams}
                    openTab={{
                      title: file?.filename,
                      iconType: 'comparison',
                    }}
                  >
                    <img
                      className=' cursor-pointer'
                      src={staticURL('assets/icons/compareRevisions.svg')}
                    />
                  </Link>
                  <div className='border-t flex-grow border-gray-300' />
                </div>
              ) : (
                <hr />
              )}
            </div>
          );
        })}

        <div className='flex justify-center my-5'>
          {contributionsConn?.loading ? (
            <Spinner />
          ) : (
            contributionsConn?.hasNextPage && (
              <Button
                text='Show more'
                onClick={() => contributionsConn.loadMore()}
                outline
              />
            )
          )}
        </div>
        <ConfirmModal
          open={showRestoreModal}
          loading={false}
          content={
            <div className='flex flex-col justify-center items-center'>
              <img src={staticURL('assets/icons/reset-big.svg')} />

              <h2 className=' mt-4'>{i18next.t('Confirm Restore File')}</h2>

              <div className=' text-wfBlack my-2 w-full'>
                <div className='text-wfBlack mt-1 flex justify-center flex-col'>
                  <p>
                    {'Are you sure you want to restore the file '}
                    <b>{file?.filename}</b>
                    {' to its previous version?'}
                  </p>
                  <br />
                  <p>
                    <b>File: </b>
                    {file?.id}
                  </p>
                  <p>
                    <b>Version: </b>#
                    {contributionForRestoreModal?.version.substring(0, 7)}
                  </p>
                  <p>
                    <b>Title: </b>
                    {contributionForRestoreModal?.title}
                  </p>
                </div>
              </div>
            </div>
          }
          onConfirm={() => {
            onRestoreFileToVersion(file, contributionForRestoreModal);
            setShowRestoreModal(false);
            setTimeout(onClosePanel, 100);
          }}
          onClose={() => {
            setShowRestoreModal(false);
          }}
        />
      </div>
    );
  },
);
